import { getSessionStorage } from './enhancedSessionStorage'

// Which system is allowed to move the viewport on a back navigation.
//
// Next owns scroll restoration here (next.config `experimental.scrollRestoration`). It snapshots
// `window.scrollY` into `sessionStorage['__next_scroll_' + key]` for the entry being left, then on
// popstate reads that pixel back and applies it in ONE shot, as soon as the destination's first
// render commits (router.js:1662-1684 → 984 → `window.scrollTo`).
//
// That is correct for a page whose full height is in its SSR markup, and wrong for a page whose
// lower half is client-fetched. Measured on pre-integ, PDP → reco product → Back, leaving at
// y=8707 of a 13797px page:
//
//   t=344ms   Next: scrollTo(0, 8707)   document is only 9670px  -> 140px from the end: FOOTER
//   t=729ms   +4016px with no scroll call at all                 -> still FOOTER  (see below)
//   t=830ms   +231px, same                                       -> still FOOTER
//   t=1113ms  our own height-aware restore: scrollTo(0, 8826)    -> correct, 770ms too late
//
// The second and third moves are the browser's scroll anchoring: the reco band regrew from a 105px
// skeleton to 4025px ABOVE the viewport, and Chrome preserves the visual by adding that growth to
// `scrollY`. It dragged the reader from 8729 to 12976 — deeper into the footer — without anything
// calling a scroll API.
//
// So a page that restores its own scroll has to be able to (a) stop Next planting a stale pixel
// before the content is back, and (b) stop the browser adjusting scroll underneath it. Neither is
// something the restore loop can out-compute; Next simply acts ~770ms earlier, which is why tuning
// the loop alone never removed the flash.
//
// `claimBackNavRestore` is (a): the page-owned restorer declares that it will restore this history
// entry, and Next's pixel for it is replaced with the top of the page. Next then performs a
// deterministic scroll to 0 instead of a clamped landing on a half-built page, and the only thing
// that positions the reader is the height-aware restore. Claiming is per-entry and opt-in, so every
// page that relies on Next's restoration (home, content pages) is untouched.
//
// `suppressScrollAnchoring` is (b), scoped to the restore window only — outside it, anchoring is
// what keeps a growing page visually stable and we want it.
//
// Ceiling: both this module and `useScrollSnapshotUnderLock` depend on Next's private
// `__next_scroll_<key>` sessionStorage format. A Next upgrade that renames it turns claiming into a
// silent no-op and the footer flash returns. The unit tests assert the exact key, so the upgrade
// that breaks it fails CI rather than shipping. [STRFRNT-15288]

// eslint-disable-next-line i18next/no-literal-string -- sessionStorage key, not user-facing copy
const NEXT_SCROLL_KEY_PREFIX = '__next_scroll_'

// A claim zeroes Next's snapshot, which lives in sessionStorage and therefore SURVIVES A RELOAD. The
// anchor that justifies the claim lives in memory and does not. So a claim can outlive its restorer
// two ways — a reload, or eviction — and each leaves Next standing down with nothing to take over:
// Back then lands at the top, which is worse than the pixel Next would have restored on its own.
//
// So every claim also records the position it was made at, in one aggregate record with the same
// lifetime as the zeroed snapshot. Whenever a claim is given up without having restored, that
// position goes back into Next's snapshot and the entry reverts to plain Next behaviour — the reader
// gets their position back, not the top of the page. [STRFRNT-15288]
// eslint-disable-next-line i18next/no-literal-string -- sessionStorage key, not user-facing copy
const OWNED_ENTRIES_KEY = '__qz_backnav_owned'

// One bound for both halves of an ownership. The anchor stores use the same number, so an entry never
// keeps one half after losing the other.
const MAX_CLAIMS = 30
const claimedEntries = new Set<string>()

type OwnedPositions = Record<string, number>

const readOwned = (): OwnedPositions => {
  const raw = getSessionStorage().getItem<string>(OWNED_ENTRIES_KEY, { isGlobal: true })
  if (typeof raw !== 'string') return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    return parsed !== null && typeof parsed === 'object' ? (parsed as OwnedPositions) : {}
  } catch {
    return {}
  }
}

const writeOwned = (owned: OwnedPositions) => {
  getSessionStorage().setItem(OWNED_ENTRIES_KEY, JSON.stringify(owned), { isGlobal: true })
}

/** Rebuilt rather than mutated — the entries are dynamic keys, which `no-dynamic-delete` rightly
 *  disallows deleting, and the record is capped at MAX_CLAIMS so copying it is free. */
const without = (owned: OwnedPositions, drop: Set<string>): OwnedPositions =>
  Object.fromEntries(Object.entries(owned).filter(([key]) => !drop.has(key)))

/** `isGlobal` so the key is written verbatim — Next reads it back with a raw
 *  `sessionStorage.getItem('__next_scroll_' + key)` (router.js:1674), so a locale prefix would hide
 *  it. The utility swallows quota/private-mode failures: restoration is best-effort and must never
 *  break a navigation. */
export const writeNextScrollSnapshot = (key: string, position: { x: number; y: number }) => {
  getSessionStorage().setItem(`${NEXT_SCROLL_KEY_PREFIX}${key}`, JSON.stringify(position), { isGlobal: true })
}

/**
 * Take over restoration of one history entry from Next, and point Next's own snapshot at the top of
 * the page so its one-shot restore cannot land on a document that has not finished growing.
 *
 * Call this at the moment the entry's anchor is recorded (`routeChangeStart`), i.e. only when the
 * caller really is going to restore it. `scrollY` is the position being taken over, kept so the entry
 * can be handed back to Next intact if this claim is ever given up unrestored.
 */
export const claimBackNavRestore = (key: string, scrollY: number) => {
  claimedEntries.delete(key) // re-insert so eviction is least-recently-claimed
  claimedEntries.add(key)
  const owned: OwnedPositions = { ...readOwned(), [key]: scrollY }
  const evicted = new Set<string>()
  if (claimedEntries.size > MAX_CLAIMS) {
    const oldest = claimedEntries.values().next().value
    if (oldest !== undefined) {
      claimedEntries.delete(oldest)
      // Evicted: no restorer will run for it any more, so give Next back the real pixel rather than
      // leaving it standing down on a zero it can never improve on.
      const evictedY = owned[oldest]
      if (evictedY !== undefined) {
        writeNextScrollSnapshot(oldest, { x: 0, y: evictedY })
        evicted.add(oldest)
      }
    }
  }
  writeOwned(evicted.size > 0 ? without(owned, evicted) : owned)
  writeNextScrollSnapshot(key, { x: 0, y: 0 })
}

/**
 * Give an entry back to Next *with its position*, because we can no longer restore it — the anchor was
 * evicted to stay under the size bound. Unlike `releaseBackNavRestore`, this rewrites Next's snapshot:
 * the reader's real position is still the right answer here, we simply are not the ones to apply it.
 */
export const surrenderBackNavRestore = (key: string) => {
  claimedEntries.delete(key)
  const owned = readOwned()
  const y = owned[key]
  if (y === undefined) return
  writeNextScrollSnapshot(key, { x: 0, y })
  writeOwned(without(owned, new Set([key])))
}

/**
 * Give an entry back to Next because there is no longer anything of ours to restore for it — the
 * reader left it at the top, so Next's own snapshot (written at `router.push`, before
 * `routeChangeStart`) already holds the truth.
 *
 * Deliberately does NOT rewrite that snapshot. Writing the remembered position here would restore the
 * reader to where they used to be rather than where they just left from, which is the same class of
 * bug in mirror image.
 */
export const releaseBackNavRestore = (key: string) => {
  const wasClaimed = claimedEntries.delete(key)
  const owned = readOwned()
  if (owned[key] !== undefined) writeOwned(without(owned, new Set([key])))
  return wasClaimed
}

/**
 * Hand back every entry claimed before this page's JavaScript started — after a reload the anchors
 * that justified those claims are gone, so without this Next stays suppressed on a zero and Back
 * lands at the top. Restores each recorded pixel, leaving those entries behaving exactly as they do
 * without any of this.
 *
 * Call once on mount, app-wide. Safe to call more than once: the record is cleared as it is consumed.
 */
export const releaseClaimsFromPreviousPageLoad = () => {
  const owned = readOwned()
  const keys = Object.keys(owned)
  if (keys.length === 0) return 0
  const handedBack = new Set<string>()
  keys.forEach(key => {
    // A claim made in THIS page's lifetime still has its anchor in memory, so leave it alone.
    if (claimedEntries.has(key)) return
    const y = owned[key]
    if (y === undefined) return
    writeNextScrollSnapshot(key, { x: 0, y })
    handedBack.add(key)
  })
  if (handedBack.size > 0) writeOwned(without(owned, handedBack))
  return handedBack.size
}

/**
 * True when a page-owned restorer has taken this entry over, so nothing else should write a pixel
 * for it. Makes the two writers order-independent: whichever of them runs second sees the claim (or
 * overwrites with the top), and the entry ends up owned either way.
 */
export const isBackNavRestoreClaimed = (key: string): boolean => claimedEntries.has(key)

/**
 * Turn off the browser's scroll anchoring for the duration of a restore, so content resolving above
 * the viewport cannot move the reader while the restore loop is deciding where they belong. Returns
 * the release, which puts back whatever inline value was there (`usePortalAsPage` sets this too, for
 * SE-1160 — releasing to `''` would clobber it).
 */
export const suppressScrollAnchoring = (): (() => void) => {
  const root = document.documentElement
  const previous = root.style.overflowAnchor
  // eslint-disable-next-line i18next/no-literal-string -- CSS keyword, not user-facing copy
  root.style.overflowAnchor = 'none'
  return () => {
    root.style.overflowAnchor = previous
  }
}
